export type PinnedTagSectionColor = 'amber' | 'indigo' | 'teal' | 'rose' | 'sky';

export interface PinnedTagSection {
  tag: string;
  label: string;
  /**
   * Keycloak group required to see this section's folder header at all.
   * A server tagged with `tag` is still visible to any viewer who has
   * registry access to it (that's governed separately, server-side) --
   * this only gates the curated zone-labeled folder wrapper. Without a
   * required group, the folder is zone-agnostic and shows for everyone
   * (not currently used, but kept optional for that case).
   */
  requiredGroup?: string;
  /**
   * Visual accent for this zone's folder header and "Jump to" chip.
   * As more zones get added over time, giving each one a distinct color
   * (instead of every folder looking the same) makes them easier to tell
   * apart at a glance. Defaults to 'amber' if omitted.
   */
  color?: PinnedTagSectionColor;
}

/**
 * Curated tag-based sections shown above the normal server grid, so a
 * deliberately-tagged group of servers (e.g. all Xero finance connections)
 * stays visible by default instead of requiring the sidebar tag filter.
 * Not a general "section per tag" mechanism — tags are used inconsistently
 * across the platform, so only explicitly curated tags belong here.
 *
 * A server tagged into one of these sections (e.g. Plane tagged `jab-mcp`)
 * can still be shared with users outside that zone -- the tag reflects the
 * server's ownership/curation, not who may use it. `requiredGroup` keeps
 * the zone-labeled folder itself from appearing for viewers who aren't in
 * that zone; they still see the same server, just without the zone folder
 * wrapped around it.
 */
export const PINNED_TAG_SECTIONS: PinnedTagSection[] = [
  { tag: 'finance', label: 'Finance', requiredGroup: 'xero-finance-readonly', color: 'amber' },
  { tag: 'jab-mcp', label: 'Just A Baby MCP Servers', requiredGroup: 'jab-mcp-only', color: 'indigo' },
  { tag: 'sales', label: 'Sales', requiredGroup: 'sales-mcp-only', color: 'rose' },
  // Vendor-hosted / natively-supported servers (e.g. GitHub, GitLab's own
  // MCP servers) that aren't owned by Geidi and aren't restricted to one
  // zone -- no requiredGroup, so this folder shows for everyone.
  { tag: 'shared-vendor-servers', label: 'Shared External Servers', color: 'sky' },
];
